import { watch, onScopeDispose, type Ref } from 'vue'

interface EmbedResizeMessage {
  type: 'videas:embed:resize'
  embedId: string
  height: number
}

/** How long a burst of changes is left to settle before the height it ends on is announced. */
const SETTLE_MS = 100

/**
 * Announce the embed's content height to the host page, which sizes the iframe to it.
 *
 * **The first height announced is the laid-out one.** The watch runs after the DOM is patched
 * (`flush: 'post'`), so the root is measured holding the player rather than the loading state the
 * embed showed while it was being read — which is what used to go out first, a 96 px spinner the
 * host then cropped the player to.
 *
 * **A later change is announced whether or not the iframe is on screen.** Chromium throttles the
 * rendering of an off-screen cross-origin iframe, and a `ResizeObserver` only reports at a rendering
 * opportunity, so a correction that relied on it alone waited for the visitor to scroll the frame
 * into view. A `MutationObserver` reports on a microtask whatever the rendering does, an image or a
 * font loading is heard as an event, and reading `scrollHeight` forces the layout synchronously —
 * so each of those re-measures, and the observer stays for what changes size with no DOM change.
 * A height equal to the last one announced is not posted again.
 */
export function useEmbedResize(options: {
  element: Ref<HTMLElement | null>
  embedId: string
  enabled?: Ref<boolean>
}): void {
  const { element, embedId, enabled } = options

  // Only activate inside an iframe
  if (typeof window === 'undefined' || window === window.parent) return

  let resizeObserver: ResizeObserver | null = null
  let mutationObserver: MutationObserver | null = null
  let observed: HTMLElement | null = null
  let settleTimer: ReturnType<typeof setTimeout> | null = null
  let lastSent: number | null = null

  function sendHeight(height: number): void {
    const message: EmbedResizeMessage = {
      type: 'videas:embed:resize',
      embedId,
      height,
    }
    window.parent.postMessage(message, '*')
  }

  function measure(): void {
    if (!observed) return
    const height = Math.ceil(observed.scrollHeight)
    // A root that is not laid out (detached, `display: none`) has nothing to announce.
    if (height <= 0 || height === lastSent) return
    lastSent = height
    sendHeight(height)
  }

  function scheduleMeasure(): void {
    if (settleTimer) clearTimeout(settleTimer)
    settleTimer = setTimeout(() => {
      settleTimer = null
      measure()
    }, SETTLE_MS)
  }

  function startObserving(el: HTMLElement): void {
    stopObserving()
    observed = el
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleMeasure)
      resizeObserver.observe(el)
    }
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(scheduleMeasure)
      mutationObserver.observe(el, { subtree: true, childList: true, attributes: true, characterData: true })
    }
    // `load` and `transitionend` do not bubble, so they are caught on the way down.
    el.addEventListener('load', scheduleMeasure, true)
    el.addEventListener('transitionend', scheduleMeasure, true)
    void document.fonts?.ready.then(() => {
      if (observed === el) measure()
    })
    measure()
  }

  function stopObserving(): void {
    if (settleTimer) {
      clearTimeout(settleTimer)
      settleTimer = null
    }
    resizeObserver?.disconnect()
    resizeObserver = null
    mutationObserver?.disconnect()
    mutationObserver = null
    if (observed) {
      observed.removeEventListener('load', scheduleMeasure, true)
      observed.removeEventListener('transitionend', scheduleMeasure, true)
      observed = null
    }
  }

  watch(
    [element, () => enabled?.value ?? true],
    ([el, isEnabled]) => {
      if (el && isEnabled) {
        if (el !== observed) startObserving(el)
      } else {
        stopObserving()
      }
    },
    { immediate: true, flush: 'post' },
  )

  onScopeDispose(stopObserving)
}
